import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from 'scbk-services';
import { AdobeDataLayerService } from 'scbk-shared-module/modules/AdobeAnalytics';
import useGlobalComponentsStore from '../stores/useGlobalComponentsStore';
import { usePopupStore } from '../stores/usePopupStore';
import { AlertDialog } from '../types/AlertDialog';
import { ConfirmDialog } from '../types/ConfirmDialog';
import { GenericDialog } from '../types/GenericDialog';
import { generatedPopupKey } from './usePopup';

type ActionType = 'cancel' | 'confirm';

const defaultActionTypesTemplate: ActionType[][] = [['confirm'], ['cancel', 'confirm']];

const useGenericDialog = () => {
  const store = useGlobalComponentsStore();

  const { t } = useTranslation();

  const alert = useCallback((alertDialog: AlertDialog) => {
    return new Promise<boolean>((resolve) => {
      const dialog: GenericDialog = {
        tag: randomTag(),
        priority: 1000,
        autoDismiss: alertDialog.autoDismiss || true,
        //icon: 'CaretDown',
        title: alertDialog.title,
        content: alertDialog.content,
        okBtnLabel: alertDialog.okBtnLabel ?? t('GenericDialog.001', '확인'),
        actions: [
          {
            //label: alertDialog?.okBtnLabel ?? '확인',
            type: `confirm`,
            onClick: () => {
              if (alertDialog.onClick) {
                alertDialog.onClick();
              }
              resolve(true);
            },
          },
        ],
        errCode: alertDialog.errCode,
        guideDesc: alertDialog.guideDesc,
      };

      if (dialog.actions.length > 0 && dialog.actions.length <= defaultActionTypesTemplate.length) {
        const defaultTypes = defaultActionTypesTemplate[dialog.actions.length - 1];
        dialog.actions.forEach((action, index) => {
          if (!action.type) {
            action.type = defaultTypes[index];
          }
        });
      }

      const sortedDialogs = [dialog, ...store.getPendingActionDialogs()].sort(
        (dialog1, dialog2) => {
          return (dialog1.priority ?? 1000) - (dialog2.priority ?? 1000);
        },
      );

      store.setPendingActionDialogs(sortedDialogs);
    });
  }, []);

  const confirm = useCallback((confirmDialog: ConfirmDialog) => {
    return new Promise<boolean>((resolve) => {
      const dialog: GenericDialog = {
        tag: randomTag(),
        priority: 1000,
        autoDismiss: true,
        //icon: 'CaretUp',
        title: confirmDialog.title ?? '',
        content: confirmDialog.content ?? '',
        okBtnLabel: confirmDialog.okBtnLabel ?? t('GenericDialog.001', '확인'),
        cancelBtnLabel: confirmDialog.cancelBtnLabel ?? t('GenericDialog.002', '취소'),
        actions: [
          {
            type: `cancel`,
            onClick: () => {
              if (confirmDialog.onCancel) {
                confirmDialog.onCancel();
              }

              resolve(false);
            },
          },
          {
            type: `confirm`,
            onClick: () => {
              if (confirmDialog.onClick) {
                confirmDialog.onClick();
              }

              resolve(true);
            },
          },
        ],
        errCode: confirmDialog.errCode,
        guideDesc: confirmDialog.guideDesc,
      };

      if (dialog.actions.length > 0 && dialog.actions.length <= defaultActionTypesTemplate.length) {
        const defaultTypes = defaultActionTypesTemplate[dialog.actions.length - 1];
        dialog.actions.forEach((action, index) => {
          if (!action.type) {
            action.type = defaultTypes[index];
          }
        });
      }

      const sortedDialogs = [dialog, ...store.getPendingActionDialogs()].sort(
        (dialog1, dialog2) => {
          return (dialog1.priority ?? 1000) - (dialog2.priority ?? 1000);
        },
      );

      store.setPendingActionDialogs(sortedDialogs);
    });
  }, []);

  const showGenericDialog = useCallback((genericDialog: GenericDialog) => {
    const dialog = {
      tag: randomTag(),
      priority: 1000,
      autoDismiss: true,
      ...genericDialog,
    };

    if (dialog.actions.length > 0 && dialog.actions.length <= defaultActionTypesTemplate.length) {
      const defaultTypes = defaultActionTypesTemplate[dialog.actions.length - 1];
      dialog.actions.forEach((action, index) => {
        if (!action.type) {
          action.type = defaultTypes[index];
        }
      });
    }

    const sortedDialogs = [dialog, ...store.getPendingActionDialogs()].sort((dialog1, dialog2) => {
      return (dialog1.priority ?? 1000) - (dialog2.priority ?? 1000);
    });

    store.setPendingActionDialogs(sortedDialogs);
    return dialog;
  }, []);

  const hide = useCallback(
    (tag: string) => {
      store.setPendingActionDialogs(
        store.getPendingActionDialogs().filter((dialog) => dialog.tag != tag),
      );
    },
    [store],
  );

  return {
    alert,
    confirm,
    showGenericDialog,
    hide,

    hideAll: () => {
      store.clearDialogs();
    },
  };
};

const randomTag = () => {
  return new Date().getTime() + '-' + Math.floor(Math.random() * 1000000);
};

/**
 *
 */
const Dialog = {
  /**
   *
   * @param options
   * @returns
   */
  alert: async (options?: AlertDialog) => {
    const store = useGlobalComponentsStore.getState();
    const popupStore = usePopupStore.getState();
    const defaultOptions: AlertDialog = {};

    const alertDialog = {
      ...defaultOptions,
      ...options,
    };

    console.log('## alertDialog options ', alertDialog);

    const tag = generatedPopupKey('alert');

    // Adobe Analytics Alert Open (S)
    if (options?.isTracking == true) {
      AdobeDataLayerService.pushDialogOpen(
        typeof alertDialog.content === 'string' ? alertDialog.content : '',
        alertDialog.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
      );
    }
    // Adobe Analytics Alert Open (E)

    return new Promise<boolean>((resolve) => {
      const dialog: GenericDialog = {
        type: 'alert',
        tag: tag,
        priority: 1000,
        autoDismiss: alertDialog.autoDismiss || true,
        //icon: 'CaretDown',
        title: alertDialog.title ?? '',
        content: alertDialog.content,
        okBtnLabel: alertDialog.okBtnLabel ?? i18n.t('GenericDialog.001', ''),
        actions: [
          {
            type: `confirm`,
            onClick: () => {
              if (options?.isTracking == true) {
                // Adobe Analytics Alert Close (S)
                AdobeDataLayerService.pushDialogClose(
                  typeof alertDialog.content === 'string' ? alertDialog.content : '',
                  alertDialog.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
                );
                // Adobe Analytics Alert Close (E)
              }
              if (alertDialog.onClick) {
                alertDialog.onClick();
              }
              resolve(true);
            },
          },
        ],
        errCode: alertDialog.errCode,
        guideDesc: alertDialog.guideDesc,
      };

      if (dialog.actions.length > 0 && dialog.actions.length <= defaultActionTypesTemplate.length) {
        const defaultTypes = defaultActionTypesTemplate[dialog.actions.length - 1];
        dialog.actions.forEach((action, index) => {
          if (!action.type) {
            action.type = defaultTypes[index];
          }
        });
      }

      const sortedDialogs = [dialog, ...store.getPendingActionDialogs()].sort(
        (dialog1, dialog2) => {
          return (dialog1.priority ?? 1000) - (dialog2.priority ?? 1000);
        },
      );

      //팝업 store에 저장
      popupStore.open(tag, { type: 'dialog' });

      store.setPendingActionDialogs(sortedDialogs);
    });
  },

  /**
   *
   * @param options
   * @returns
   */
  confirm: async (options?: ConfirmDialog) => {
    const store = useGlobalComponentsStore.getState();
    const popupStore = usePopupStore.getState();

    const tag = generatedPopupKey('confirm');

    const defaultOptions: ConfirmDialog = {
      content: options?.content ?? '',
      okBtnLabel: options?.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
      cancelBtnLabel: options?.cancelBtnLabel ?? i18n.t('GenericDialog.002', '취소'),
    };

    const confirmDialog = {
      ...defaultOptions,
      ...options,
    };

    // Adobe Analytics Confirm Open (S)
    AdobeDataLayerService.pushDialogOpen(
      typeof confirmDialog.content === 'string' ? confirmDialog.content : '',
      confirmDialog.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
      confirmDialog.cancelBtnLabel ?? i18n.t('GenericDialog.002', '취소'),
    );
    // Adobe Analytics Confirm Open (E)

    console.log('## confirmDialog options ', confirmDialog);

    return new Promise<boolean>((resolve) => {
      const dialog: GenericDialog = {
        type: 'confirm',
        tag: tag,
        priority: 1000,
        autoDismiss: true,
        //icon: 'CaretUp',
        title: confirmDialog.title ?? '',
        content: confirmDialog.content ?? '',
        okBtnLabel: confirmDialog.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
        cancelBtnLabel: confirmDialog.cancelBtnLabel ?? i18n.t('GenericDialog.002', '취소'),
        actions: [
          {
            type: `cancel`,
            onClick: () => {
              if (confirmDialog.onCancel) {
                confirmDialog.onCancel();
              }

              resolve(false);
            },
          },
          {
            type: `confirm`,
            onClick: () => {
              // Adobe Analytics Confirm Close (S)
              AdobeDataLayerService.pushDialogClose(
                typeof confirmDialog.content === 'string' ? confirmDialog.content : '',
                confirmDialog.okBtnLabel ?? i18n.t('GenericDialog.001', '확인'),
              );
              // Adobe Analytics Confirm Close (E)
              if (confirmDialog.onClick) {
                confirmDialog.onClick();
              }

              resolve(true);
            },
          },
        ],
        errCode: confirmDialog.errCode,
        guideDesc: confirmDialog.guideDesc,
      };

      if (dialog.actions.length > 0 && dialog.actions.length <= defaultActionTypesTemplate.length) {
        const defaultTypes = defaultActionTypesTemplate[dialog.actions.length - 1];
        dialog.actions.forEach((action, index) => {
          if (!action.type) {
            action.type = defaultTypes[index];
          }
        });
      }

      const sortedDialogs = [dialog, ...store.getPendingActionDialogs()].sort(
        (dialog1, dialog2) => {
          return (dialog1.priority ?? 1000) - (dialog2.priority ?? 1000);
        },
      );

      //팝업 store에 저장
      popupStore.open(tag, { type: 'dialog' });

      store.setPendingActionDialogs(sortedDialogs);
    });
  },
};

export { Dialog, useGenericDialog };
